import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Save, Camera } from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhotoVideoEventForm = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const isEdit = !!eventId;

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadEvent();
    }
  }, [eventId]);

  const loadEvent = async () => {
    try {
      const response = await axios.get(`${API}/photo-video-events/${eventId}`, {
        headers: getAuthHeaders()
      });
      const event = response.data.event;
      setFormData({
        name: event.name,
        description: event.description || ''
      });
    } catch (error) {
      toast.error('Failed to load event');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Event name is required');
      return;
    }

    setLoading(true);

    try {
      if (isEdit) {
        await axios.put(`${API}/photo-video-events/${eventId}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Event updated successfully!');
      } else {
        await axios.post(`${API}/photo-video-events`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Event created successfully!');
      }
      navigate('/photo-video-events');
    } catch (error) {
      toast.error(isEdit ? 'Failed to update event' : 'Failed to create event');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/photo-video-events')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            {isEdit ? 'Edit Event' : 'Create Photo/Video Event'}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Event Info */}
          <Card className="p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Event Details</h2>
                <p className="text-sm text-slate-400">People can upload photos and videos with QR code</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Event Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., John's Birthday Party, Wedding 2024"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this event..."
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>

            <div className="mt-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
              <h3 className="text-sm font-semibold text-slate-300 mb-2">📸 How it works:</h3>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Generate a QR code for your event</li>
                <li>• Share the QR code or link with guests</li>
                <li>• Guests scan and upload photos/videos (no login required)</li>
                <li>• Everyone can view and download all media</li>
                <li>• 10MB max per file</li>
              </ul>
            </div>
          </Card>

          {/* Submit */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white py-6 text-lg font-semibold shadow-lg shadow-pink-500/50"
            >
              <Save className="w-5 h-5 mr-2" />
              {loading ? 'Saving...' : (isEdit ? 'Update Event' : 'Create Event')}
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/photo-video-events')}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white px-8"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PhotoVideoEventForm;
